/**
 * Provysn Risk Question Management
 * Allows GRC Admin / Super Admin to manage the risk questionnaire:
 * - Add / Edit / Delete questions
 * - Toggle visibility (show/hide from app owners)
 * - Filter by category
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { authFetchInit, getAuthHeaders, hasAuthSession } from '../utils/authSession';
import { listingTableSx } from '../components/listingTableSx';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Switch,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Badge,
  Autocomplete,
  Grid,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Visibility,
  VisibilityOff,
  Assessment,
  Search,
  FilterList,
  HelpOutline,
  DragIndicator,
  CreateNewFolder as AddCategoryIcon,
  DriveFileRenameOutline as RenameIcon,
  TuneOutlined as ScopeIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as CircleIcon,
  CloudDone as CloudDoneIcon,
  CloudUpload as CloudUploadIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { UI_TOKENS } from '../constants/uiTokens';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { API_ROOT } from '../config/apiConfig';
import { ListingPagination, ListingSortSelect } from '../components/ListingPagination';
import { DEFAULT_LISTING_SORT, ListingSortValue, sortListing } from '../utils/listingSort';
import * as XLSX from 'xlsx';
import {
  questionCategories,
  allCategoryNames,
  responseOptionsInputValue,
  responseOptionsFromFormLines,
  responseOptionsRawToText,
  getExcelCell,
  parsePipeDelimitedOptions,
  distinctFrameworkLabelsFromQuestions,
  questionMatchesTableFrameworkFilter,
  questionFrameworkTags,
  FRAMEWORK_FILTER_UNTAGGED,
  getFrameworkChipSx,
  normalizeFrameworkLabel,
} from '../utils/riskQuestionnaire';

interface Question {
  id: string;
  template_id: string;
  question_number: number;
  question_text: string;
  category: string;
  categories?: string[];
  response_type: 'text' | 'numeric' | 'yes_no' | 'dropdown' | 'multi_select';
  response_options?: string;
  risk_weight: number;
  notes?: string;
  help_text?: string;
  is_required: boolean;
  order_index: number;
  is_visible?: boolean;
  external_id?: string;
  applicable_frameworks?: string[];
  evidence_upload?: string;
}

// Categories are derived dynamically from questions (no hardcoded list)
const RESPONSE_TYPES = [
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'numeric', label: 'Numeric' },
  { value: 'text', label: 'Free Text' },
  { value: 'multi_select', label: 'Multi-Select' },
];

const responseTypeColor = (type: string): 'success' | 'primary' | 'secondary' | 'warning' | 'info' | 'default' => {
  switch (type) {
    case 'yes_no': return 'success';
    case 'dropdown': return 'primary';
    case 'numeric': return 'secondary';
    case 'multi_select': return 'warning';
    case 'text': return 'info';
    default: return 'default';
  }
};

/** Parse weight from API/DB (may be string or decimal); null if missing/invalid. */
function parseRiskWeight(w: unknown): number | null {
  if (w === null || w === undefined || w === '') return null;
  const n = typeof w === 'number' ? w : parseFloat(String(w).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** Display weight without trailing zeros (e.g. 9 not 9.0000). */
function formatRiskWeightDisplay(w: unknown): string {
  const n = parseRiskWeight(w);
  if (n === null) return '—';
  if (Math.abs(n - Math.round(n)) < 1e-6) return String(Math.round(n));
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

const RiskQuestionManagement: React.FC = () => {
  const { user } = useAuth();
  const { resolvedMode } = useTheme();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Question | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);
  /** Empty = no category filter (show all). Non-empty = questions in any selected category. */
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  /** Table filter bar only (does not change owner questionnaire scope). */
  const [tableFrameworkFilter, setTableFrameworkFilter] = useState<string[]>([]);
  const [frameworkFilter, setFrameworkFilter] = useState<string[]>([]);
  /** `undefined` until template loads; `null` = no restriction (all frameworks for owners); else persisted list (may differ spelling from catalog until normalized). */
  const [activeFrameworksDb, setActiveFrameworksDb] = useState<string[] | null | undefined>(undefined);
  const initialFrameworkSyncDone = useRef(false);
  /** Owner-scope auto-save indicator — purely cosmetic so the user sees the persistence loop work. */
  const [scopeSaveState, setScopeSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [formData, setFormData] = useState<Partial<Question>>({
    question_text: '',
    category: 'General',
    categories: ['General'],
    response_type: 'yes_no',
    risk_weight: 5,
    is_required: true,
    order_index: 1,
    help_text: '',
    notes: '',
    response_options: '',
    applicable_frameworks: [],
  });

  // Category management
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [catDialogMode, setCatDialogMode] = useState<'add' | 'rename'>('add');
  const [catDialogTarget, setCatDialogTarget] = useState(''); // category being renamed
  const [catDialogValue, setCatDialogValue] = useState('');
  const [catDeleteConfirm, setCatDeleteConfirm] = useState<string | null>(null);

  const [templateId, setTemplateId] = useState<string>('');
  const canManage = user?.role === 'super_admin' || user?.role === 'grc_admin';
  const [bulkUploading, setBulkUploading] = useState(false);

  const authHeaders = (): Record<string, string> =>
    getAuthHeaders({ 'Content-Type': 'application/json' });

  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const loadQuestions = async (tid?: string) => {
    const id = tid || templateId;
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_ROOT}/risk-assessment/questions/${id}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        const list = (data.questions || []).map((q: Question) => {
          const cats = questionCategories(q);
          return { ...q, categories: cats, category: cats[0] };
        });
        setQuestions(list);
      }
    } catch {
      showSnack('Failed to load questions', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManage) return;
    // Fetch the active template so we manage the same questions users see
    fetch(`${API_ROOT}/risk-assessment/template`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        const id = data?.template?.id;
        const rawAf = data?.template?.active_frameworks;
        const parsed =
          Array.isArray(rawAf) && rawAf.length > 0
            ? rawAf.map((s: unknown) => String(s).trim()).filter(Boolean)
            : null;
        setActiveFrameworksDb(parsed);
        if (id) {
          setTemplateId(id);
          loadQuestions(id);
        } else {
          setActiveFrameworksDb(undefined);
        }
      })
      .catch(() => showSnack('Failed to load active template', 'error'));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const frameworks = useMemo(() => distinctFrameworkLabelsFromQuestions(questions), [questions]);

  useEffect(() => {
    initialFrameworkSyncDone.current = false;
  }, [templateId]);

  const frameworkNormKey = (arr: string[] | null | undefined) =>
    (arr == null ? [] : arr).map((s) => normalizeFrameworkLabel(s)).filter(Boolean).sort().join('\0');

  /** Align chip state with template.active_frameworks and catalog spelling. */
  useEffect(() => {
    if (activeFrameworksDb === undefined) return;
    if (frameworks.length === 0) {
      setFrameworkFilter([]);
      return;
    }
    if (activeFrameworksDb === null) {
      setFrameworkFilter([...frameworks]);
      initialFrameworkSyncDone.current = true;
      return;
    }
    const catNorm = new Map(frameworks.map((f) => [normalizeFrameworkLabel(f), f]));
    const mapped = activeFrameworksDb
      .map((s) => catNorm.get(normalizeFrameworkLabel(String(s))))
      .filter((f): f is string => Boolean(f));
    const next = mapped.length ? mapped : [...frameworks];
    setFrameworkFilter(next);
    if (mapped.length === 0) {
      setActiveFrameworksDb(null);
    } else if (frameworkNormKey(next) !== frameworkNormKey(activeFrameworksDb)) {
      setActiveFrameworksDb(next);
    }
    initialFrameworkSyncDone.current = true;
  }, [frameworks, activeFrameworksDb]);

  useEffect(() => {
    if (!templateId || !canManage) return;
    if (activeFrameworksDb === undefined) return;
    if (!initialFrameworkSyncDone.current) return;
    if (frameworks.length === 0) return;

    const isAll =
      frameworkFilter.length > 0 &&
      frameworkFilter.length === frameworks.length &&
      frameworks.every((f) =>
        frameworkFilter.some((x) => normalizeFrameworkLabel(x) === normalizeFrameworkLabel(f))
      );

    const catNorm = new Map(frameworks.map((f) => [normalizeFrameworkLabel(f), f]));
    const canonical = (sel: string[]) => {
      const out: string[] = [];
      const seen = new Set<string>();
      for (const s of sel) {
        const c = catNorm.get(normalizeFrameworkLabel(String(s)));
        if (!c) continue;
        const k = normalizeFrameworkLabel(c);
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(c);
      }
      return out;
    };

    const nextDb: string[] | null = isAll ? null : canonical(frameworkFilter);
    const matches =
      (nextDb === null && activeFrameworksDb === null) ||
      (nextDb !== null &&
        activeFrameworksDb !== null &&
        frameworkNormKey(nextDb) === frameworkNormKey(activeFrameworksDb));

    if (matches) return;

    const t = setTimeout(() => {
      setScopeSaveState('saving');
      fetch(`${API_ROOT}/risk-assessment/admin/template/active-frameworks`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ active_frameworks: nextDb }),
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(await res.text());
          setActiveFrameworksDb(nextDb);
          setScopeSaveState('saved');
          window.setTimeout(() => setScopeSaveState('idle'), 1800);
        })
        .catch(() => {
          setScopeSaveState('error');
          showSnack('Failed to save framework selection', 'error');
        });
    }, 600);
    return () => clearTimeout(t);
  }, [frameworkFilter, frameworks, templateId, canManage, activeFrameworksDb]);

  const questionsForTable = useMemo(
    () => questions.filter((q) => questionMatchesTableFrameworkFilter(q, tableFrameworkFilter)),
    [questions, tableFrameworkFilter]
  );

  /** Drop category selections that no longer exist under the current table filters. */
  useEffect(() => {
    const names = allCategoryNames(questionsForTable);
    const nameSet = new Set(names);
    setSelectedCategories((prev) => prev.filter((c) => nameSet.has(c)));
  }, [questionsForTable]);

  const frameworkFilterCounts = useMemo(() => {
    const counts: Record<string, number> = { all: questions.length };
    let untagged = 0;
    questions.forEach((q) => {
      const tags = questionFrameworkTags(q);
      if (tags.length === 0) {
        untagged += 1;
        return;
      }
      tags.forEach((fw) => {
        counts[fw] = (counts[fw] || 0) + 1;
      });
    });
    counts[FRAMEWORK_FILTER_UNTAGGED] = untagged;
    return counts;
  }, [questions]);

  // Filtered questions (table framework filter, then search + category)
  const filteredQuestions = useMemo(() => {
    return questionsForTable.filter((q) => {
      const qc = questionCategories(q);
      const matchesSearch =
        !searchTerm ||
        q.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
        qc.some((c) => c.toLowerCase().includes(searchTerm.toLowerCase())) ||
        questionFrameworkTags(q).some((fw) => fw.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesCat =
        selectedCategories.length === 0 || qc.some((c) => selectedCategories.includes(c));
      return matchesSearch && matchesCat;
    });
  }, [questionsForTable, searchTerm, selectedCategories]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedCategories, tableFrameworkFilter, rowsPerPage, listingSort]);

  const sortedQuestions = sortListing(filteredQuestions, listingSort, {
    getName: (q) => q.question_text,
    getDate: (q) => (q as { created_at?: string; updated_at?: string }).created_at
      || (q as { created_at?: string; updated_at?: string }).updated_at,
  });
  const totalPages = Math.max(1, Math.ceil(sortedQuestions.length / rowsPerPage));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const paginatedQuestions = sortedQuestions.slice(startIndex, startIndex + rowsPerPage);

  // Category counts for filter badges (scoped to table framework filter)
  const catCounts = useMemo(() => {
    const counts: Record<string, number> = { all: questionsForTable.length };
    questionsForTable.forEach((q) => {
      questionCategories(q).forEach((c) => {
        counts[c] = (counts[c] || 0) + 1;
      });
    });
    return counts;
  }, [questionsForTable]);

  const handleOpenDialog = (question?: Question, defaultCategory?: string) => {
    if (question) {
      setEditingQuestion(question);
      const cats = questionCategories(question);
      const { response_options: roRaw, ...questionRest } = question;
      setFormData({
        ...questionRest,
        categories: cats,
        category: cats[0],
        response_options: responseOptionsInputValue(roRaw) as Question['response_options'],
      });
    } else {
      setEditingQuestion(null);
      const dc = defaultCategory || 'General';
      setFormData({
        question_text: '',
        category: dc,
        categories: [dc],
        response_type: 'yes_no',
        risk_weight: 5,
        is_required: true,
        order_index: questions.length + 1,
        help_text: '',
        notes: '',
        response_options: '',
        applicable_frameworks: [],
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.question_text?.trim()) {
      showSnack('Question text is required', 'error');
      return;
    }
    const rawCats = formData.categories?.length
      ? Array.from(new Set(formData.categories.map(c => String(c).trim()).filter(Boolean)))
      : [];
    const cats = rawCats.length ? rawCats : ['General'];
    const ro = responseOptionsFromFormLines(
      String(formData.response_type || 'yes_no'),
      formData.response_options
    );
    const frameworkTags = Array.from(
      new Set((formData.applicable_frameworks || []).map((f) => String(f).trim()).filter(Boolean))
    );
    const payload = {
      ...formData,
      category: cats[0],
      categories: cats,
      response_options: ro,
      applicable_frameworks: frameworkTags,
    };
    try {
      if (editingQuestion) {
        const res = await fetch(`${API_ROOT}/risk-assessment/question/${editingQuestion.id}`, {
          method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error();
        showSnack('Question updated successfully');
      } else {
        const res = await fetch(`${API_ROOT}/risk-assessment/question`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ ...payload, template_id: templateId })
        });
        if (!res.ok) throw new Error();
        showSnack('Question added successfully');
      }
      setDialogOpen(false);
      loadQuestions();
    } catch {
      showSnack('Failed to save question', 'error');
    }
  };

  const handleDelete = async (question: Question) => {
    try {
      const res = await fetch(`${API_ROOT}/risk-assessment/question/${question.id}`, {
        method: 'DELETE', headers: authHeaders()
      });
      if (!res.ok) throw new Error();
      setDeleteConfirm(null);
      showSnack('Question deleted');
      loadQuestions();
    } catch {
      showSnack('Failed to delete question', 'error');
    }
  };

  const handleToggleVisibility = async (q: Question) => {
    try {
      const res = await fetch(`${API_ROOT}/risk-assessment/question/${q.id}/visibility`, {
        method: 'PATCH', headers: authHeaders(),
        body: JSON.stringify({ isVisible: !q.is_visible })
      });
      if (!res.ok) throw new Error();
      loadQuestions();
    } catch {
      showSnack('Failed to toggle visibility', 'error');
    }
  };

  const downloadSampleExcel = () => {
    // Static file shipped with the frontend build
    const a = document.createElement('a');
    a.href = '/enhanced_compliance_questions.xlsx';
    a.download = 'enhanced_compliance_questions.xlsx';
    a.click();
  };

  const parseExcelFile = async (file: File): Promise<Array<Partial<Question>>> => {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheet = wb.SheetNames[0];
    const rows: any[] = XLSX.utils.sheet_to_json(wb.Sheets[sheet], { defval: '' });

    const toBool = (v: any) => String(v || '').trim().toLowerCase() === 'yes' || String(v || '').trim().toLowerCase() === 'true';
    const mapInput = (t: any): Question['response_type'] => {
      const s = String(t || '').trim().toLowerCase();
      if (s === 'yes/no' || s === 'yes-no') return 'yes_no';
      if (s === 'dropdown') return 'dropdown';
      if (s === 'multi-select' || s === 'multi select' || s === 'multi_select') return 'multi_select';
      if (s === 'numeric' || s === 'number') return 'numeric';
      if (s === 'text' || s === 'free text' || s === 'free_text') return 'text';
      return 'yes_no';
    };

    return rows
      .map((r, idx) => {
        const row = r as Record<string, unknown>;
        const catParts = String(getExcelCell(row, 'Category') || 'General')
          .split(/[|;]/)
          .map((s: string) => s.trim())
          .filter(Boolean);
        const cats = catParts.length ? catParts : ['General'];
        const optsRaw = getExcelCell(
          row,
          'Allowed Values / Options',
          'Allowed Values',
          'Options',
          'Dropdown Options',
          'Allowed values / options'
        );
        const opts = parsePipeDelimitedOptions(optsRaw);
        return {
        external_id: String(getExcelCell(row, 'ID') || `q${idx + 1}`).trim(),
        category: cats[0],
        categories: cats,
        question_text: String(getExcelCell(row, 'Question') || '').trim(),
        response_type: mapInput(getExcelCell(row, 'Input Type')),
        risk_weight: Number(getExcelCell(row, 'Weight') || 5),
        is_required: toBool(getExcelCell(row, 'Required')),
        is_visible: toBool(getExcelCell(row, 'Visible')),
        applicable_frameworks: String(getExcelCell(row, 'Applicable Frameworks') || '')
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean),
        help_text: String(getExcelCell(row, 'Description') || '').trim(),
        evidence_upload: String(getExcelCell(row, 'Evidence Upload Required') || '').trim(),
        response_options: opts ? JSON.stringify(opts) : ''
      };
      })
      .filter(q => q.question_text);
  };

  const handleBulkUpload = async (file: File) => {
    try {
      setBulkUploading(true);
      const parsed = await parseExcelFile(file);
      const normalized = parsed.map(q => ({
        ...q,
        response_options: q.response_options ? JSON.parse(String(q.response_options)) : null
      }));

      const res = await fetch(`${API_ROOT}/risk-assessment/admin/questions/bulk`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ questions: normalized })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Bulk import failed');
      const ins = data.inserted ?? 0;
      const skip = data.skipped_duplicates ?? 0;
      const parts = [`Imported ${ins} question(s)`];
      if (skip > 0) parts.push(`skipped ${skip} duplicate(s) (same external ID or same question text as existing or earlier row)`);
      showSnack(parts.join(' · '));
      loadQuestions();
    } catch (e: any) {
      showSnack(e?.message || 'Bulk import failed', 'error');
    } finally {
      setBulkUploading(false);
    }
  };

  // Derived category list from questions visible under the current framework selection
  const categories = useMemo(() => allCategoryNames(questionsForTable), [questionsForTable]);

  const openAddCategory = () => {
    setCatDialogMode('add');
    setCatDialogValue('');
    setCatDialogOpen(true);
  };

  const openRenameCategory = (cat: string) => {
    setCatDialogMode('rename');
    setCatDialogTarget(cat);
    setCatDialogValue(cat);
    setCatDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    const newName = catDialogValue.trim();
    if (!newName) return;

    if (catDialogMode === 'add') {
      setCatDialogOpen(false);
      setSelectedCategories([newName]);
      handleOpenDialog(undefined, newName);
      return;
    }

    // Rename: update all questions that include this category tag
    if (catDialogMode === 'rename' && catDialogTarget !== newName) {
      const toRename = questions.filter(q => questionCategories(q).includes(catDialogTarget));
      try {
        await Promise.all(
          toRename.map(q => {
            const newCats = Array.from(new Set(questionCategories(q).map(c => (c === catDialogTarget ? newName : c))));
            return fetch(`${API_ROOT}/risk-assessment/question/${q.id}`, {
              method: 'PUT',
              headers: authHeaders(),
              body: JSON.stringify({ ...q, category: newCats[0], categories: newCats }),
            });
          })
        );
        showSnack(`Category renamed to "${newName}"`);
        setSelectedCategories(prev => prev.map(c => (c === catDialogTarget ? newName : c)));
        loadQuestions();
      } catch {
        showSnack('Failed to rename category', 'error');
      }
    }
    setCatDialogOpen(false);
  };

  const handleDeleteCategory = async (cat: string) => {
    const touched = questions.filter(q => questionCategories(q).includes(cat));
    try {
      await Promise.all(
        touched.map(q => {
          let newCats = questionCategories(q).filter(c => c !== cat);
          if (newCats.length === 0) newCats = ['General'];
          return fetch(`${API_ROOT}/risk-assessment/question/${q.id}`, {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify({ ...q, category: newCats[0], categories: newCats }),
          });
        })
      );
      showSnack(`Removed category "${cat}" from ${touched.length} question(s)`);
      setCatDeleteConfirm(null);
      setSelectedCategories(prev => prev.filter(c => c !== cat));
      loadQuestions();
    } catch {
      showSnack('Failed to update questions for category removal', 'error');
    }
  };

  if (!canManage) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        Tag questions with the <strong>AI Governance</strong> framework (Applicable frameworks) to include them in AI
        asset risk questionnaires. Risk tier is calculated automatically from answers and weights when asset owners
        save or submit.
      </Alert>
      {/* Owner scope picker — saves to template.active_frameworks (NOT a table filter) */}
      {frameworks.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            mb: 2,
            borderRadius: 2,
            borderColor: 'info.light',
            bgcolor: 'info.50',
            p: 1.75,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: 'info.main',
                color: 'common.white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ScopeIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.2 }}>
                  Frameworks in scope for application owners
                </Typography>
                {scopeSaveState === 'saving' && (
                  <Chip
                    size="small"
                    icon={<CloudUploadIcon sx={{ fontSize: '0.85rem !important' }} />}
                    label="Saving…"
                    sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'background.paper' }}
                  />
                )}
                {scopeSaveState === 'saved' && (
                  <Chip
                    size="small"
                    color="success"
                    icon={<CloudDoneIcon sx={{ fontSize: '0.85rem !important' }} />}
                    label="Saved"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
                These are the standards that appear in the owner-facing risk questionnaire. Untagged questions
                always appear. <strong>This does not change what you see in the table below</strong> — use the
                Frameworks filter further down to narrow this admin view.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <Button
                size="small"
                variant="text"
                onClick={() => setFrameworkFilter([...frameworks])}
                disabled={
                  frameworkFilter.length === frameworks.length &&
                  frameworks.every((f) =>
                    frameworkFilter.some((x) => normalizeFrameworkLabel(x) === normalizeFrameworkLabel(f))
                  )
                }
                sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25 }}
              >
                Select all
              </Button>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', pl: 5.25 }}>
            {frameworks.map((fw) => {
              const selectionForUi = frameworkFilter.length > 0 ? frameworkFilter : frameworks;
              const selected = selectionForUi.some(
                (p) => normalizeFrameworkLabel(p) === normalizeFrameworkLabel(fw)
              );
              return (
                <Chip
                  key={fw}
                  label={fw}
                  clickable
                  icon={
                    selected ? (
                      <CheckCircleIcon sx={{ fontSize: '0.95rem !important', color: 'info.main !important' }} />
                    ) : (
                      <CircleIcon sx={{ fontSize: '0.95rem !important', color: 'text.disabled !important' }} />
                    )
                  }
                  onClick={() =>
                    setFrameworkFilter((prev) => {
                      const isOn = prev.some(
                        (p) => normalizeFrameworkLabel(p) === normalizeFrameworkLabel(fw)
                      );
                      if (!isOn) return [...prev, fw];
                      if (prev.length <= 1) return [...frameworks];
                      return prev.filter((x) => normalizeFrameworkLabel(x) !== normalizeFrameworkLabel(fw));
                    })
                  }
                  size="small"
                  variant={selected ? 'filled' : 'outlined'}
                  sx={{
                    height: 26,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: selected ? 'background.paper' : 'transparent',
                    borderColor: selected ? 'info.main' : 'divider',
                    color: selected ? 'text.primary' : 'text.secondary',
                    '&:hover': { bgcolor: 'background.paper', borderColor: 'info.main' },
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment sx={{ color: 'primary.main', fontSize: 22 }} />
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 }}>Risk Questions</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              Configure the questionnaire that application owners fill out to determine risk level
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={downloadSampleExcel}
            sx={{ fontSize: '0.78rem', py: 0.5 }}
          >
            Download sample Excel
          </Button>

          <Button
            component="label"
            size="small"
            variant="outlined"
            disabled={bulkUploading}
            sx={{ fontSize: '0.78rem', py: 0.5 }}
          >
            {bulkUploading ? 'Importing…' : 'Bulk import'}
            <input
              type="file"
              accept=".xlsx"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleBulkUpload(f);
                e.currentTarget.value = '';
              }}
            />
          </Button>

          <Button
            size="small"
            variant="contained"
            startIcon={<Add sx={{ fontSize: '0.9rem !important' }} />}
            onClick={() => handleOpenDialog()}
            sx={{ fontSize: '0.78rem', py: 0.5 }}
          >
            Add Question
          </Button>
        </Box>
      </Box>

      {/* Filters + Category Management */}
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, p: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
            <TextField
              size="small"
              placeholder="Search questions…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: '1rem', color: 'text.disabled' }} /></InputAdornment> }}
              sx={{ width: 240, '& .MuiOutlinedInput-root': { height: 32, fontSize: '0.82rem' } }}
            />
            <ListingSortSelect
              value={listingSort}
              onChange={setListingSort}
              sx={{ '& .MuiInputLabel-root': { fontSize: '0.82rem' }, '& .MuiSelect-select': { fontSize: '0.82rem' } }}
            />
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', ml: 'auto' }}>
              {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''}
              {tableFrameworkFilter.length > 0 ? ' (framework filter)' : ''}
              {' · '}
              {questionsForTable.filter((q) => q.is_visible !== false).length} published ·{' '}
              {questionsForTable.filter((q) => q.is_visible === false).length} draft
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>CATEGORIES</Typography>
            <Tooltip title="Click categories to multi-select. “All” clears the filter.">
              <HelpOutline sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
            <Tooltip title="Add new category">
              <IconButton size="small" onClick={openAddCategory} sx={{ p: 0.5 }}>
                <AddCategoryIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              key="all"
              label={`All (${catCounts.all})`}
              size="small"
              variant={selectedCategories.length === 0 ? 'filled' : 'outlined'}
              color={selectedCategories.length === 0 ? 'primary' : 'default'}
              onClick={() => setSelectedCategories([])}
              sx={{ height: 24, fontSize: '0.68rem', cursor: 'pointer' }}
            />
            {categories.map(cat => {
              const selected = selectedCategories.includes(cat);
              return (
                <Chip
                  key={cat}
                  label={`${cat} (${catCounts[cat] || 0})`}
                  size="small"
                  variant={selected ? 'filled' : 'outlined'}
                  color={selected ? 'primary' : 'default'}
                  onClick={() =>
                    setSelectedCategories(prev =>
                      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
                    )
                  }
                  sx={{ height: 24, fontSize: '0.68rem', cursor: 'pointer' }}
                />
              );
            })}
            {/* Rename / Delete when exactly one category is selected */}
            {selectedCategories.length === 1 && (
              <>
                <Tooltip title={`Rename "${selectedCategories[0]}"`}>
                  <IconButton size="small" sx={{ p: 0.25 }} onClick={() => openRenameCategory(selectedCategories[0])}>
                    <RenameIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={`Remove "${selectedCategories[0]}" from all questions`}>
                  <IconButton size="small" sx={{ p: 0.25, color: 'error.main' }} onClick={() => setCatDeleteConfirm(selectedCategories[0])}>
                    <Delete sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>

          {frameworks.length > 0 && (
            <>
              <Divider sx={{ my: 1.25 }} />
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', mb: 0.75 }}>
                <FilterList sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.4 }}>
                  FILTER TABLE BY FRAMEWORK
                </Typography>
                <Tooltip title="Narrows only this admin table. Owner-facing scope is set in the blue card above.">
                  <HelpOutline sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                </Tooltip>
                {tableFrameworkFilter.length > 0 && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setTableFrameworkFilter([])}
                    sx={{ fontSize: '0.68rem', textTransform: 'none', py: 0, minHeight: 0, ml: 0.5 }}
                  >
                    Clear ({tableFrameworkFilter.length})
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip
                  label={`All (${frameworkFilterCounts.all})`}
                  size="small"
                  variant={tableFrameworkFilter.length === 0 ? 'filled' : 'outlined'}
                  color={tableFrameworkFilter.length === 0 ? 'primary' : 'default'}
                  onClick={() => setTableFrameworkFilter([])}
                  sx={{ height: 24, fontSize: '0.68rem', cursor: 'pointer' }}
                />
                {(frameworkFilterCounts[FRAMEWORK_FILTER_UNTAGGED] ?? 0) > 0 && (
                  <Chip
                    label={`Untagged (${frameworkFilterCounts[FRAMEWORK_FILTER_UNTAGGED]})`}
                    size="small"
                    onClick={() =>
                      setTableFrameworkFilter((prev) =>
                        prev.includes(FRAMEWORK_FILTER_UNTAGGED)
                          ? prev.filter((x) => x !== FRAMEWORK_FILTER_UNTAGGED)
                          : [...prev, FRAMEWORK_FILTER_UNTAGGED]
                      )
                    }
                    sx={{
                      height: 24,
                      fontSize: '0.68rem',
                      cursor: 'pointer',
                      ...getFrameworkChipSx('Untagged', {
                        selected: tableFrameworkFilter.includes(FRAMEWORK_FILTER_UNTAGGED),
                        height: 24,
                        fontSize: '0.68rem',
                        maxWidth: 160,
                        mode: resolvedMode,
                      }),
                    }}
                  />
                )}
                {frameworks.map((fw) => {
                  const selected = tableFrameworkFilter.includes(fw);
                  return (
                    <Chip
                      key={fw}
                      label={`${fw} (${frameworkFilterCounts[fw] || 0})`}
                      size="small"
                      onClick={() =>
                        setTableFrameworkFilter((prev) =>
                          prev.includes(fw) ? prev.filter((x) => x !== fw) : [...prev, fw]
                        )
                      }
                      sx={{
                        height: 24,
                        fontSize: '0.68rem',
                        cursor: 'pointer',
                        ...getFrameworkChipSx(fw, { selected, height: 24, fontSize: '0.68rem', maxWidth: 180, mode: resolvedMode }),
                      }}
                    />
                  );
                })}
              </Box>
            </>
          )}
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" stickyHeader sx={listingTableSx}>
              <TableHead>
                <TableRow sx={{ '& th': { backgroundColor: 'grey.50', fontWeight: 700, fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '2px solid', borderColor: 'divider', py: 1.25 } }}>
                  <TableCell width={40}>#</TableCell>
                  <TableCell width={110}>Category</TableCell>
                  <TableCell width={150}>Frameworks</TableCell>
                  <TableCell>Question</TableCell>
                  <TableCell width={110}>Type</TableCell>
                  <TableCell width={80} align="center">Weight</TableCell>
                  <TableCell width={80} align="center">Required</TableCell>
                  <TableCell width={80} align="center">Published</TableCell>
                  <TableCell width={100} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQuestions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>
                      {questions.length === 0 ? 'No questions yet — click Add Question to get started.' : 'No questions match your filters.'}
                    </TableCell>
                  </TableRow>
                ) : paginatedQuestions.map((q, idx) => {
                  const weightNum = parseRiskWeight(q.risk_weight);
                  const weightColor =
                    weightNum === null
                      ? 'text.secondary'
                      : weightNum >= 10
                        ? 'error.main'
                        : weightNum >= 5
                          ? 'warning.main'
                          : 'success.main';
                  return (
                  <TableRow
                    key={q.id}
                    sx={{
                      backgroundColor: q.is_visible === false ? 'grey.50' : 'background.paper',
                      opacity: q.is_visible === false ? 0.6 : 1,
                      '&:hover': { backgroundColor: 'primary.50' },
                      '& td': { py: 1, fontSize: '0.82rem', borderColor: 'divider' }
                    }}
                  >
                    <TableCell sx={{ color: 'text.disabled', fontWeight: 600 }}>{startIndex + idx + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                        {questionCategories(q).map(c => (
                          <Chip
                            key={c}
                            label={c}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                        {questionFrameworkTags(q).length > 0 ? (
                          questionFrameworkTags(q).map((fw) => (
                            <Tooltip key={fw} title={fw} arrow>
                              <Chip
                                label={fw}
                                size="small"
                                sx={getFrameworkChipSx(fw, { maxWidth: 140, mode: resolvedMode })}
                              />
                            </Tooltip>
                          ))
                        ) : (
                          <Tooltip title="No framework tags — question applies when any framework is in scope" arrow>
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                              All
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" title={q.question_text} sx={{ fontSize: '0.82rem', lineHeight: 1.4 }}>
                        {q.question_text}
                      </Typography>
                      {q.help_text && (
                        <Tooltip title={q.help_text} arrow>
                          <Typography variant="caption" color="info.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25, cursor: 'help', fontSize: '0.65rem' }}>
                            <HelpOutline sx={{ fontSize: '0.7rem' }} /> Hint available
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={RESPONSE_TYPES.find(t => t.value === q.response_type)?.label || q.response_type}
                        size="small" color={responseTypeColor(q.response_type)}
                        sx={{ height: 20, fontSize: '0.62rem', '& .MuiChip-label': { px: 0.75 } }} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption" sx={{ fontWeight: 700, color: weightColor }}>
                        {formatRiskWeightDisplay(q.risk_weight)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={q.is_required ? 'Yes' : 'No'} size="small"
                        color={q.is_required ? 'error' : 'default'} variant={q.is_required ? 'filled' : 'outlined'}
                        sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.6 } }} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={q.is_visible !== false ? 'Published for owners. Click to move it back to draft.' : 'Draft. Owners do not see it until it is published.'}>
                        <Switch
                          size="small"
                          checked={q.is_visible !== false}
                          onChange={() => handleToggleVisibility(q)}
                          color="primary"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                        <Tooltip title="Edit question">
                          <IconButton size="small" onClick={() => handleOpenDialog(q)}
                            sx={{ width: 26, height: 26, '&:hover': { color: 'primary.main', backgroundColor: 'primary.50' } }}>
                            <Edit sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete question">
                          <IconButton size="small" onClick={() => setDeleteConfirm(q)}
                            sx={{ width: 26, height: 26, '&:hover': { color: 'error.main', backgroundColor: 'error.50' } }}>
                            <Delete sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <ListingPagination
            page={safePage}
            totalPages={totalPages}
            totalItems={filteredQuestions.length}
            pageSize={rowsPerPage}
            onPageChange={setPage}
            onPageSizeChange={setRowsPerPage}
            pageSizeSelectSx={{
              '& .MuiInputBase-input': { fontSize: '0.82rem' },
              '& .MuiInputLabel-root': { fontSize: '0.82rem' },
              '& .MuiSelect-select': { fontSize: '0.82rem' },
            }}
          />
        </Paper>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assessment sx={{ color: 'primary.main', fontSize: 18 }} />
            <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
              {editingQuestion ? `Edit Question #${editingQuestion.question_number}` : 'Add New Question'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setDialogOpen(false)} aria-label="Close">
            <CancelIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HelpOutline sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Basics
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                required
                multiline
                rows={2}
                label="Question Text"
                value={formData.question_text || ''}
                onChange={e => setFormData(p => ({ ...p, question_text: e.target.value }))}
                sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                size="small"
                disableCloseOnSelect
                options={categories}
                getOptionLabel={o => (typeof o === 'string' ? o : String(o))}
                value={formData.categories?.length ? formData.categories : [formData.category || 'General']}
                onChange={(_, newValue) => {
                  const flat = newValue
                    .map(v => (typeof v === 'string' ? v : String(v)))
                    .map(s => s.trim())
                    .filter(Boolean);
                  const uniq = Array.from(new Set(flat));
                  const next = uniq.length ? uniq : ['General'];
                  setFormData(p => ({ ...p, categories: next, category: next[0] }));
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Categories"
                    placeholder="Pick tags or type a new category and press Enter"
                    helperText="One or more categories — use Enter to add a new tag"
                    sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm } }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <ScopeIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Response
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl size="small" fullWidth sx={{ '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm } }}>
                <InputLabel>Response Type</InputLabel>
                <Select
                  value={formData.response_type || 'yes_no'}
                  label="Response Type"
                  onChange={e => {
                    const rt = e.target.value as Question['response_type'];
                    setFormData(p => {
                      const next: Partial<Question> = { ...p, response_type: rt };
                      if (rt !== 'dropdown' && rt !== 'multi_select') {
                        next.response_options = '';
                      } else if (typeof p.response_options !== 'string') {
                        next.response_options = responseOptionsRawToText(p.response_options) as Question['response_options'];
                      }
                      return next;
                    });
                  }}
                  MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: UI_TOKENS.text.bodySm, minHeight: UI_TOKENS.menu.itemMinHeight, py: UI_TOKENS.menu.itemPaddingY } } } }}
                >
                  {RESPONSE_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                fullWidth
                type="number"
                label="Risk Weight"
                value={formData.risk_weight ?? 5}
                onChange={e => setFormData(p => ({ ...p, risk_weight: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 0, max: 30 }}
                helperText="Higher = more impact on risk score"
                sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiFormHelperText-root': { fontSize: UI_TOKENS.text.helper } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                fullWidth
                type="number"
                label="Order"
                value={formData.order_index ?? 1}
                onChange={e => setFormData(p => ({ ...p, order_index: parseInt(e.target.value) || 1 }))}
                inputProps={{ min: 1 }}
                sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm } }}
              />
            </Grid>
            {(formData.response_type === 'dropdown' || formData.response_type === 'multi_select') && (
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  rows={4}
                  label="Options (one per line)"
                  value={responseOptionsInputValue(formData.response_options)}
                  onChange={e => setFormData(p => ({ ...p, response_options: e.target.value }))}
                  placeholder={'SaaS\nOn-Prem\nHybrid'}
                  helperText="Each line becomes a selectable option"
                  sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiFormHelperText-root': { fontSize: UI_TOKENS.text.helper } }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <RenameIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Notes
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                label="Help Text (shown as hint to app owners)"
                value={formData.help_text || ''}
                onChange={e => setFormData(p => ({ ...p, help_text: e.target.value }))}
                sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                label="Internal Notes (GRC team only)"
                value={formData.notes || ''}
                onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl size="small" fullWidth sx={{ '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm } }}>
                <InputLabel>Required</InputLabel>
                <Select
                  value={formData.is_required === false ? 'no' : 'yes'}
                  label="Required"
                  onChange={e => setFormData(p => ({ ...p, is_required: e.target.value === 'yes' }))}
                  MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: UI_TOKENS.text.bodySm, minHeight: UI_TOKENS.menu.itemMinHeight, py: UI_TOKENS.menu.itemPaddingY } } } }}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setDialogOpen(false)} sx={{ fontSize: UI_TOKENS.text.bodySm }}>
            Cancel
          </Button>
          <Button size="small" onClick={handleSave} variant="contained" sx={{ fontSize: UI_TOKENS.text.bodySm }}>
            {editingQuestion ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>Delete Question?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete <strong>"{deleteConfirm?.question_text}"</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            sx={{ borderRadius: 1.5 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Add/Rename Dialog */}
      <Dialog open={catDialogOpen} onClose={() => setCatDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>
          {catDialogMode === 'add' ? 'Add New Category' : `Rename "${catDialogTarget}"`}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Category Name"
            value={catDialogValue}
            onChange={e => setCatDialogValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSaveCategory(); }}
            sx={{ mt: 1 }}
          />
          {catDialogMode === 'add' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              A new question form will open with this category pre-filled.
            </Typography>
          )}
          {catDialogMode === 'rename' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              All {questions.filter(q => questionCategories(q).includes(catDialogTarget)).length} questions that include this category will be updated.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setCatDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCategory} disabled={!catDialogValue.trim()}>
            {catDialogMode === 'add' ? 'Create & Add Question' : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Delete Confirmation */}
      <Dialog open={Boolean(catDeleteConfirm)} onClose={() => setCatDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Remove Category</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>"{catDeleteConfirm}"</strong> from every question that uses it (
            <strong>{questions.filter(q => questionCategories(q).includes(catDeleteConfirm || '')).length}</strong>{' '}
            question(s))? Questions with no categories left will be assigned <strong>General</strong>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setCatDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error"
            onClick={() => catDeleteConfirm && handleDeleteCategory(catDeleteConfirm)}>
            Remove Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RiskQuestionManagement;
